// CRA inlines process.env.REACT_APP_* and process.env.NODE_ENV at build time.
// Production default: same-origin ("" → fetch "/api/..."), so Nginx /api proxy works
// without baking a hostname into the bundle. Local default: localhost:3001.
const fromEnv = process.env.REACT_APP_API_BASE;
const API_BASE = (
  fromEnv != null && String(fromEnv).trim() !== ""
    ? String(fromEnv).trim()
    : process.env.NODE_ENV === "production"
      ? ""
      : "http://localhost:3001"
).replace(/\/$/, "");

const SERVER_DOWN_HU =
  "A szerver jelenleg nem elérhető. Indítsd el a Solarya servert (általában a 3001-es porton), majd próbáld újra.";

function humanizeHttpError(status, fallback) {
  if (status === 401) return fallback || "Bejelentkezés szükséges, vagy a megadott adatok hibásak.";
  if (status === 403) return "Nincs jogosultság ehhez a művelethez.";
  if (status === 404) return "A kért erőforrás nem található.";
  if (status === 409) return fallback || "Ez az adat már foglalt.";
  if (status === 423) return fallback || "A fiók ideiglenesen zárolva van.";
  if (status === 429) return "Túl sok kérés. Kérlek, várj egy kicsit, majd próbáld újra.";
  if (status >= 500) return "A szerveren hiba történt. Próbáld újra később.";
  return fallback || "Ismeretlen hiba történt.";
}

function looksEnglishOrTechnical(text) {
  if (!text || typeof text !== "string") return true;
  const t = text.trim();
  if (!t) return true;
  // Server already sends Hungarian business errors — keep those.
  if (/[áéíóöőúüűÁÉÍÓÖŐÚÜŰ]/.test(t)) return false;
  const lower = t.toLowerCase();
  if (
    lower.startsWith("http ") ||
    lower.includes("failed to fetch") ||
    lower.includes("internal server error") ||
    lower.includes("bad request") ||
    lower.includes("unauthorized") ||
    lower.includes("forbidden") ||
    lower.includes("not found") ||
    lower.includes("network") ||
    /^error[:\s]/i.test(t)
  ) {
    return true;
  }
  // Short ASCII-only technical blobs → replace
  if (/^[A-Za-z0-9 _\-./:]+$/.test(t) && t.length < 80) {
    // Allow known HU messages without accents that we might send later — none yet.
    // Keep message if it contains common HU words without accents:
    if (
      /\b(hiba|sikertelen|ervenytelen|jelszo|email|fiok|regisztr|bejelentkez)/i.test(
        t
      )
    ) {
      return false;
    }
    return true;
  }
  return false;
}

async function api(path, { method = "GET", body, headers } = {}) {
  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      credentials: "include",
      headers: {
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
        ...headers,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (err) {
    const wrapped = new Error(SERVER_DOWN_HU);
    wrapped.status = 0;
    wrapped.cause = err;
    throw wrapped;
  }

  let data = null;
  const text = await res.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { error: text };
    }
  }

  if (!res.ok) {
    const serverMsg =
      (typeof data?.error === "string" && data.error) ||
      (typeof data?.message === "string" && data.message) ||
      "";
    const message = looksEnglishOrTechnical(serverMsg)
      ? humanizeHttpError(res.status, serverMsg || null)
      : serverMsg;

    const err = new Error(message);
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return data;
}

export const authApi = {
  me: () => api("/api/auth/me"),
  register: (payload) =>
    api("/api/auth/register", { method: "POST", body: payload }),
  login: (payload) => api("/api/auth/login", { method: "POST", body: payload }),
  logout: () => api("/api/auth/logout", { method: "POST", body: {} }),
  verifyEmail: (token) =>
    api("/api/auth/verify-email", { method: "POST", body: { token } }),
  resendVerification: () =>
    api("/api/auth/resend-verification", { method: "POST", body: {} }),
  forgotPassword: (email) =>
    api("/api/auth/forgot-password", { method: "POST", body: { email } }),
  resetPassword: (token, password) =>
    api("/api/auth/reset-password", {
      method: "POST",
      body: { token, password },
    }),
  changePassword: (currentPassword, newPassword) =>
    api("/api/auth/change-password", {
      method: "POST",
      body: { currentPassword, newPassword },
    }),
  updateProfile: (payload) =>
    api("/api/auth/profile", { method: "PATCH", body: payload }),
  addAddress: (payload) =>
    api("/api/auth/addresses", { method: "POST", body: payload }),
  updateAddress: (id, payload) =>
    api(`/api/auth/addresses/${id}`, { method: "PATCH", body: payload }),
  deleteAddress: (id) =>
    api(`/api/auth/addresses/${id}`, { method: "DELETE" }),
  deleteAccount: (password) =>
    api("/api/auth/account", { method: "DELETE", body: { password } }),
};

export { API_BASE, SERVER_DOWN_HU };
